import React, { useState, useEffect, useMemo } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    CommandBar,
    Stack,
    Text,
    DatePicker,
    Dropdown,
    Pivot,
    PivotItem,
    Spinner,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';

import Summary from './Summary';
import {
    useAdminApi,
    toDateString,
    fromDateString,
    formatDate,
    formatDateTime,
    formatNumber,
    percent,
    daysBefore,
    daysBetween,
    downloadCsv,
    homeStyles,
    sectionStyles,
    statStyles,
    mutedText,
} from '../common';

// funnel ผู้สมัครแยกตามช่องทาง (attribution Phase 2) ข้อมูลจาก GET /accounts/funnel
// และแท็บสรุปรายวัน (Phase 3) ข้อมูลจาก GET /marketing/summary ใช้ช่วงวันเดียวกัน

// ข้อมูลที่มาเริ่มเก็บวันนี้ บัญชีก่อนหน้าอยู่ในกลุ่ม "ก่อนเริ่มเก็บ"
const TRACKING_START = '2026-10-26';
// ใบเสนอราคาอัตโนมัติเริ่มเก็บที่มาวันนี้
const QUOTATION_TRACKING_START = '2026-10-27';
const MAX_DAYS = 92;

const TRACKING_START_TEXT = formatDate(fromDateString(TRACKING_START));
const QUOTATION_TRACKING_START_TEXT = formatDate(fromDateString(QUOTATION_TRACKING_START));

const CHANNEL_LABELS = {
    'google-ads': 'Google Ads',
    'microsoft-ads': 'Microsoft Ads',
    'facebook-ads': 'Facebook Ads',
    'instagram-ads': 'Instagram Ads',
    'tiktok-ads': 'TikTok Ads',
    'youtube-ads': 'YouTube Ads',
    'linkedin-ads': 'LinkedIn Ads',
    'line-ads': 'LINE Ads',
    'organic-search': 'ค้นหา (ไม่จ่ายเงิน)',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'tiktok': 'TikTok',
    'youtube': 'YouTube',
    'line': 'LINE',
    'linkedin': 'LinkedIn',
    'x': 'X',
    'email': 'อีเมล',
    'partner': 'พาร์ทเนอร์',
    'referral': 'เว็บอื่น',
    'direct': 'เข้าตรง / ไม่ทราบ',
    'before-tracking': `ก่อนเริ่มเก็บ (ก่อน ${TRACKING_START_TEXT})`,
};

const HEARD_FROM_LABELS = {
    'google': 'Google',
    'microsoft': 'Microsoft (Bing)',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'tiktok': 'TikTok',
    'youtube': 'YouTube',
    'line': 'LINE',
    'linkedin': 'LinkedIn',
    'friend': 'เพื่อนแนะนำ',
    'accountant': 'สำนักงานบัญชี',
    'event': 'งานสัมมนา / อีเวนต์',
    'other': 'อื่นๆ',
    'not-answered': 'ไม่ได้ตอบ',
};

const STAGE_LABELS = {
    signup: 'สมัครแล้ว ยังไม่สร้างบริษัท',
    company: 'สร้างบริษัทแล้ว ยังไม่ออกเอกสาร',
    document: 'ออกเอกสารแล้ว ยังไม่จ่ายเงิน',
    paid: 'จ่ายเงินแล้ว',
};

function channelLabel(channel) {
    return CHANNEL_LABELS[channel] || channel;
}

function heardFromLabel(heardFrom) {
    return HEARD_FROM_LABELS[heardFrom] || heardFrom;
}

function downloadAccounts(rows, from, to) {
    const header = ['วันที่สมัคร', 'อีเมล', 'ชื่อ', 'เบอร์โทร', 'บริษัท', 'เลขผู้เสียภาษี', 'ช่องทาง', 'source', 'medium',
        'campaign', 'content', 'referrer', 'landing page', 'รู้จักจากไหน', 'อื่นๆ (ระบุ)', 'ขั้น boarding', 'สถานะ', 'แพ็กเกจ',
        'จำนวนเอกสาร', 'ออกเอกสารแรก', 'จ่ายเงินครั้งแรก', 'ยอดก่อน VAT'];

    downloadCsv(`funnel-${from}-${to}.csv`, header, rows.map((row) => [
        formatDateTime(row.createdTime), row.email, row.name, row.mobile, row.companyName, row.taxId, channelLabel(row.channel),
        row.source, row.medium, row.campaign, row.content, row.referrer, row.landingPage,
        row.heardFrom ? heardFromLabel(row.heardFrom) : '', row.heardFromOther, row.boardingStep, STAGE_LABELS[row.stage],
        row.billingsType, row.documentCount, formatDateTime(row.firstDocumentTime), formatDateTime(row.firstPaidTime), row.paidValue,
    ]));
}

// คอลัมน์ของตาราง funnel ใช้ร่วมกันทั้งตามช่องทาง / รู้จักจากไหน / campaign
function funnelColumns(keyName, header, render) {
    const number = (fieldName, name) => ({
        key: fieldName, name, fieldName, minWidth: 90, maxWidth: 110,
        onRender: (item) => <Text>{formatNumber(item[fieldName])}</Text>,
    });

    return [
        { key: keyName, name: header, fieldName: keyName, minWidth: 200, maxWidth: 280, isResizable: true, onRender: (item) => <Text>{render(item[keyName])}</Text> },
        number('signup', 'สมัคร'),
        number('company', 'สร้างบริษัท'),
        { key: 'companyRate', name: '% สร้างบริษัท', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{percent(item.company, item.signup)}</Text> },
        number('document', 'ออกเอกสาร'),
        number('paid', 'จ่ายเงิน'),
        { key: 'paidRate', name: '% จ่ายเงิน', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{percent(item.paid, item.signup)}</Text> },
        number('paidValue', 'ยอดก่อน VAT'),
    ];
}

// คอลัมน์ funnel ใบเสนอราคา: ขอใบเสนอราคา → จ่ายเงินภายใน 90 วัน
function quotationColumns(keyName, header, render) {
    return [
        { key: keyName, name: header, fieldName: keyName, minWidth: 200, maxWidth: 280, isResizable: true, onRender: (item) => <Text>{render(item[keyName])}</Text> },
        { key: 'quotation', name: 'ใบเสนอราคา', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{formatNumber(item.quotation)}</Text> },
        { key: 'paid', name: 'จ่ายเงิน', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{formatNumber(item.paid)}</Text> },
        { key: 'paidRate', name: '% จ่ายเงิน', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{percent(item.paid, item.quotation)}</Text> },
        { key: 'paidValue', name: 'ยอดก่อน VAT', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{formatNumber(item.paidValue)}</Text> },
    ];
}

const accountColumns = [
    { key: 'createdTime', name: 'วันที่สมัคร', minWidth: 120, maxWidth: 130, onRender: (item) => <Text>{formatDateTime(item.createdTime)}</Text> },
    { key: 'email', name: 'อีเมล', fieldName: 'email', minWidth: 180, maxWidth: 240, isResizable: true },
    { key: 'mobile', name: 'เบอร์โทร', fieldName: 'mobile', minWidth: 100, maxWidth: 110 },
    { key: 'companyName', name: 'บริษัท', fieldName: 'companyName', minWidth: 160, maxWidth: 240, isResizable: true },
    { key: 'channel', name: 'ช่องทาง', minWidth: 130, maxWidth: 170, isResizable: true, onRender: (item) => <Text>{channelLabel(item.channel)}</Text> },
    { key: 'campaign', name: 'Campaign', fieldName: 'campaign', minWidth: 120, maxWidth: 180, isResizable: true },
    { key: 'heardFrom', name: 'รู้จักจากไหน', minWidth: 110, maxWidth: 150, isResizable: true, onRender: (item) => <Text>{item.heardFrom ? heardFromLabel(item.heardFrom) + (item.heardFromOther ? ': ' + item.heardFromOther : '') : ''}</Text> },
    { key: 'stage', name: 'สถานะ', minWidth: 200, maxWidth: 230, onRender: (item) => <Text>{STAGE_LABELS[item.stage]}</Text> },
    { key: 'boardingStep', name: 'boarding', fieldName: 'boardingStep', minWidth: 70, maxWidth: 80 },
    { key: 'documentCount', name: 'เอกสาร', minWidth: 60, maxWidth: 70, onRender: (item) => <Text>{formatNumber(item.documentCount)}</Text> },
    { key: 'paidValue', name: 'ยอดก่อน VAT', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{item.paid ? formatNumber(item.paidValue) : ''}</Text> },
];

const Funnel = () => {

    const request = useAdminApi();

    const today = new Date();

    const [tab, setTab] = useState('funnel');
    const [fromDate, setFromDate] = useState(daysBefore(today, 29));
    const [toDate, setToDate] = useState(today);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [stageFilter, setStageFilter] = useState('not-paid');
    const [channelFilter, setChannelFilter] = useState('all');

    const from = toDateString(fromDate);
    const to = toDateString(toDate);
    const days = daysBetween(fromDate, toDate);
    const validRange = days >= 1 && days <= MAX_DAYS;

    useEffect(() => {
        if (!validRange) {
            setError(`เลือกช่วงได้ไม่เกิน ${MAX_DAYS} วัน`);
            setData(null);
            return;
        }

        if (tab !== 'funnel') return;

        setLoading(true);
        setError('');

        request('get', '/accounts/funnel?from=' + from + '&to=' + to)
            .then((response) => setData(response.data.data))
            .catch((err) => {
                console.log('Funnel error: ', err);
                setError('โหลดข้อมูลไม่สำเร็จ');
                setData(null);
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, from, to, refresh, tab]);

    const channelOptions = useMemo(() => [
        { key: 'all', text: 'ทุกช่องทาง' },
        ...(data ? data.byChannel.map((item) => ({ key: item.channel, text: channelLabel(item.channel) })) : []),
    ], [data]);

    const filteredAccounts = useMemo(() => {
        if (!data) return [];

        return data.accounts.filter((item) => {
            if (channelFilter !== 'all' && item.channel !== channelFilter) return false;
            if (stageFilter === 'all') return true;
            if (stageFilter === 'not-paid') return item.stage !== 'paid';
            return item.stage === stageFilter;
        });
    }, [data, stageFilter, channelFilter]);

    const stats = data ? [
        { label: 'สมัคร', value: data.totals.signup },
        { label: 'สร้างบริษัท', value: data.totals.company, rate: percent(data.totals.company, data.totals.signup) },
        { label: 'ออกเอกสาร', value: data.totals.document, rate: percent(data.totals.document, data.totals.signup) },
        { label: 'จ่ายเงิน', value: data.totals.paid, rate: percent(data.totals.paid, data.totals.signup) },
        { label: 'ยอดก่อน VAT (บาท)', value: data.totals.paidValue },
    ] : [];

    const quotations = data && data.quotations;

    const commandBarItems = [
        {
            key: 'refresh',
            text: 'รีเฟรช',
            iconProps: { iconName: 'Refresh' },
            disabled: loading,
            onClick: () => setRefresh(refresh + 1),
        },
        {
            key: 'tracking',
            text: 'ตั้งแต่เริ่มเก็บที่มา',
            iconProps: { iconName: 'Calendar' },
            disabled: loading,
            onClick: () => {
                const start = fromDateString(TRACKING_START);
                setFromDate(start < daysBefore(today, MAX_DAYS - 1) ? daysBefore(today, MAX_DAYS - 1) : start);
                setToDate(today);
            },
        },
        ...(tab === 'funnel' ? [{
            key: 'csv',
            text: 'ดาวน์โหลด CSV',
            iconProps: { iconName: 'Download' },
            disabled: loading || filteredAccounts.length === 0,
            onClick: () => downloadAccounts(filteredAccounts, from, to),
        }] : []),
    ];

    return (
        <div style={homeStyles}>
            <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '2%' } }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 10 }}>
                    <Stack>
                        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>Funnel ตามช่องทาง</Text>
                        <Text styles={mutedText}>
                            {tab === 'funnel'
                                ? `นับบัญชีที่สมัครในช่วงที่เลือก แล้วดูว่าไปถึงขั้นไหนจนถึงวันนี้ ช่องทางมาจากการเข้าเว็บครั้งแรก (เก็บตั้งแต่ ${TRACKING_START_TEXT})`
                                : 'ค่าโฆษณาเทียบกับ lead ลูกค้าจ่ายเงิน และรายได้ ของช่วงที่เลือก'}
                        </Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                        <DatePicker label="ตั้งแต่" value={fromDate} maxDate={toDate} formatDate={formatDate}
                            onSelectDate={(date) => date && setFromDate(date)} styles={{ root: { width: 160 } }} />
                        <DatePicker label="ถึง" value={toDate} minDate={fromDate} maxDate={today} formatDate={formatDate}
                            onSelectDate={(date) => date && setToDate(date)} styles={{ root: { width: 160 } }} />
                    </Stack>
                </Stack>

                <Pivot selectedKey={tab} onLinkClick={(item) => setTab(item.props.itemKey)}>
                    <PivotItem headerText="Funnel ตามช่องทาง" itemKey="funnel" />
                    <PivotItem headerText="สรุปรายวัน" itemKey="summary" />
                </Pivot>

                <CommandBar items={commandBarItems} styles={{ root: { padding: 0, background: 'transparent' } }} />

                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

                {tab === 'summary' && validRange && <Summary from={from} to={to} refresh={refresh} />}

                {tab === 'funnel' && loading && <Spinner label="กำลังโหลด..." />}

                {tab === 'funnel' && data && !loading && (
                    <>
                        <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                            {stats.map((stat) => (
                                <Stack key={stat.label} styles={statStyles}>
                                    <Text styles={mutedText}>{stat.label}</Text>
                                    <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>{formatNumber(stat.value)}</Text>
                                    {stat.rate && <Text styles={mutedText}>{stat.rate} ของผู้สมัคร</Text>}
                                </Stack>
                            ))}
                        </Stack>
                        {data.internalRemoved > 0 && (
                            <Text styles={mutedText}>ไม่นับบัญชีภายใน / ทดสอบ {formatNumber(data.internalRemoved)} บัญชี</Text>
                        )}

                        <Stack styles={sectionStyles}>
                            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>ตามช่องทาง</Text>
                            <DetailsList items={data.byChannel} columns={funnelColumns('channel', 'ช่องทาง', channelLabel)}
                                selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                        </Stack>

                        <Stack styles={sectionStyles}>
                            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>รู้จักจากไหน (ลูกค้าตอบเอง)</Text>
                            <DetailsList items={data.byHeardFrom} columns={funnelColumns('heardFrom', 'คำตอบ', heardFromLabel)}
                                selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                        </Stack>

                        <Stack styles={sectionStyles}>
                            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>ตาม campaign (utm_campaign หรือแคมเปญในทะเบียน)</Text>
                            {data.byCampaign.length > 0
                                ? <DetailsList items={data.byCampaign} columns={funnelColumns('campaign', 'Campaign', (value) => value)}
                                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                                : <Text styles={{ root: { color: '#605E5C', paddingTop: 10 } }}>ยังไม่มีบัญชีที่มาจากลิงก์ที่ติด utm_campaign หรือแคมเปญในทะเบียนในช่วงนี้</Text>}
                        </Stack>

                        {quotations && (
                            <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                                    ขอใบเสนอราคา: {formatNumber(quotations.totals.quotation)} ใบ · จ่ายเงิน {formatNumber(quotations.totals.paid)} ({percent(quotations.totals.paid, quotations.totals.quotation)}) · {formatNumber(quotations.totals.paidValue)} บาท
                                </Text>
                                <Text styles={mutedText}>
                                    ใบเสนอราคาอัตโนมัติที่ลูกค้าขอจากเว็บ นับว่าจ่ายเงินเมื่อมีใบเสร็จค่าบริการภายใน 90 วัน ที่มาเก็บตั้งแต่ {QUOTATION_TRACKING_START_TEXT}
                                    (ลูกค้าที่ทั้งสมัครและขอใบเสนอราคาจะเห็นในทั้งสองส่วน)
                                </Text>
                                {quotations.byChannel.length > 0 && (
                                    <DetailsList items={quotations.byChannel} columns={quotationColumns('channel', 'ช่องทาง', channelLabel)}
                                        selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                                )}
                                {quotations.byCampaign.length > 0 && (
                                    <DetailsList items={quotations.byCampaign} columns={quotationColumns('campaign', 'Campaign', (value) => value)}
                                        selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                                )}
                            </Stack>
                        )}

                        <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>รายชื่อลูกค้า ({formatNumber(filteredAccounts.length)})</Text>
                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    <Dropdown label="สถานะ" selectedKey={stageFilter} styles={{ root: { width: 240 } }}
                                        onChange={(event, option) => setStageFilter(option.key)}
                                        options={[
                                            { key: 'not-paid', text: 'ยังไม่จ่ายเงินทั้งหมด' },
                                            ...Object.entries(STAGE_LABELS).map(([key, text]) => ({ key, text })),
                                            { key: 'all', text: 'ทั้งหมด' },
                                        ]} />
                                    <Dropdown label="ช่องทาง" selectedKey={channelFilter} styles={{ root: { width: 220 } }}
                                        onChange={(event, option) => setChannelFilter(option.key)} options={channelOptions} />
                                </Stack>
                            </Stack>
                            <DetailsList items={filteredAccounts} columns={accountColumns} getKey={(item) => item.oid}
                                selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                        </Stack>
                    </>
                )}
            </Stack>
        </div>
    );
};

export default Funnel;
